import * as yup from 'yup';
import { luhnCheck } from 'helpers/paymentCard';


const getCCNumRegExp = (issuer) => {
  if (issuer === 'visa') {
    return /^4[0-9]{12}([0-9]{3})?$/;
  }

  if (issuer === 'mastercard') {
    return /^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$/;
  }

  if (issuer === 'amex') {
    return /^3[47][0-9]{13}$/;
  }

  if (issuer === 'discover') {
    return /^(6011((0|9|[2-4])[0-9]{11,14}|(74|7[7-9]|8[6-9])[0-9]{10,13})|6(4[4-9][0-9]{13,16}|5[0-9]{14,17}))/;
  }

  if (issuer === 'unionpay') {
    return /^622(1(2[6-9][0-9]{10,13}|[3-9][0-9]{11,14})|[3-8][0-9]{12,15}|9([[0-1][0-9]{11,14}|2[0-5][0-9]{10,13}))|62[4-6][0-9]{13,16}|628[2-8][0-9]{12,15}/;
  }

  if (issuer === 'jcb') {
    return /^35(2[8-9][0-9]{12,15}|[3-8][0-9]{13,16})/;
  }

  return /^/;
};

export const ccValidationSchema = ({issuer, maxLength}) => (
  yup.object({
    firstName: yup
      .string()
      .required('Field is required'),
    lastName: yup
      .string()
      .required('Field is required'),
    company: yup
      .string(),
    ccNum: yup
      .string()
      .max(maxLength, `Max Card number length should be ${maxLength} digits`)
      .test('ccNum', 'Credit card number not valid', (val) => {
        const ccNumRegExp = getCCNumRegExp(issuer);
        const match = val ? val?.replace(/\s/g, '').match(ccNumRegExp) : true;
        return Boolean(match) && luhnCheck(val);
      })
      .required('Field is required'),
    expiry: yup
      .string()
      .typeError('Not a valid expiration date. Example: MM/YY')
      .max(5, 'Not a valid expiration date. Example: MM/YY')
      .matches(
        /([0-9]{2})\/([0-9]{2})/,
        'Not a valid expiration date. Example: MM/YY'
      )
      .test(
        'test-credit-card-expiration-date',
        'Invalid Expiration Date has past',
        (expirationDate) => {
          if (!expirationDate) {
            return false;
          }

          const today = new Date();
          const monthToday = today.getMonth() + 1;
          const yearToday = today
            .getFullYear()
            .toString()
            .substr(-2);

          const [expMonth, expYear] = expirationDate.split('/');

          if (Number(expYear) < Number(yearToday)) {
            return false;
          } else if (
            Number(expMonth) < monthToday &&
            Number(expYear) <= Number(yearToday)
          ) {
            return false;
          }

          return true;
        }
      )
      .test(
        'test-credit-card-expiration-date',
        'Invalid Expiration Month',
        (expirationDate) => {
          if (!expirationDate) {
            return false;
          }

          const [expMonth] = expirationDate.split('/');

          if (Number(expMonth) > 12) {
            return false;
          }

          return true;
        }
      )
      .required('Field is required'),
    cvv: yup
      .string()
      .matches(/^[0-9]{3}|[0-9]{4}$/, 'Invalid CVV')
      .required('Field is required'),
    street: yup
      .array()
      .min(1)
      .of(yup
        .string()
        .test('street1', 'Field is required', (value, values) => {
          if (values.path === 'street[0]') {
            return !!value?.length;
          }

          return true;
        })
      )
      .required('Field is required'),
    city: yup
      .string()
      .required('Field is required'),
    regionId: yup
      .string()
      .required('Field is required'),
    postcode: yup
      .string()
      .matches(/(^\d{5}$)/, 'Invalid zip code')
      .required('Field is required'),
    telephone: yup
      .string()
      .matches(/^(\()?\d{3}(\))?(-|\s)?\d{3}(-|\s)\d{4}$/, 'Phone number must be formatted as (###)-###-####')
      .required('Field is required')
  })
);
